import { EditIcon, PlusIcon, TrashIcon } from "@xola/icons";
import { Alert, Button, Currency, FormGroup, Label, Modal, Select, SubmitButton, Switch } from "@xola/ui-kit";
import clsx from "clsx";
import { FormField, InputField, requiredNumberMoreThanZero, requiredString } from "common";
import { FieldArray, useFormikContext } from "formik";
import { produce } from "immer";
import { get, isEqual, uniqueId } from "lodash";
import React, { useEffect, useState } from "react";
import SimpleBar from "simplebar-react";
import { LineItem, LineItemDestination, LineItemType, Purchase } from "types";
import { useDebounce } from "use-debounce";
import { array, object } from "yup";
import shallow from "zustand/shallow";
import { ScrollToFieldError } from "../../helpers/validationHelper";
import { useSeller } from "../../hooks/useAccount";
import { CreatePurchaseItemState, useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { NoTranslate } from "../Common/NoTranslate";
import { QuantitySpinner } from "../Common/QuantitySpinner/QuantitySpinner";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { CurrencyInputField } from "../Field/CurrencyInputField";
import { CreatePurchaseBreakdown } from "./CreatePurchaseBreakdown";
import { createPurchaseStoreToPayload, useUpdatePurchase } from "./PurchaseManager";
import { usePurchaseResponseStore } from "./store/usePurchaseResponseStore";
import { findItemIndex, isPackageItem } from "@/models/Purchase";

interface FormValues {
    lineItems: Array<Partial<LineItem>>;
}

interface CustomLineItemListProps {
    item?: CreatePurchaseItemState;
    itemIndex?: number;
}

export const CustomLineItemList = ({ item: pkgItem, itemIndex: index }: CustomLineItemListProps) => {
    const seller = useSeller();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const activeItemIndex = useCreatePurchaseStore((state) => state.activeItemIndex);
    const itemCusomLineItems = useCreatePurchaseStore((state) => state.getActiveItem().customLineItems, shallow);
    const itemIndex = index ?? activeItemIndex;
    const setPurchase = useCreatePurchaseStore((state) => state.actions.setPurchase);

    const enableCustomLineItems = useCreatePurchaseStore((state) => {
        return state.purchase.items[itemIndex]?.enableCustomLineItems;
    });

    const handleChangeQuantity = (index: number, quantity: number) => {
        setPurchase((purchase) => {
            const lineItem = purchase.items[itemIndex].customLineItems[index];
            lineItem.quantity = quantity;
            lineItem.amount = lineItem.quantity * lineItem.price!;
        });
    };

    const handleToggleEnableCustomLineItems = () => {
        setPurchase((purchase) => {
            purchase.items[itemIndex].enableCustomLineItems = !enableCustomLineItems;
        });
    };

    const hasCustomLineItems = pkgItem ? pkgItem.customLineItems?.length > 0 : itemCusomLineItems?.length > 0;

    const customeLineItems = pkgItem?.customLineItems ?? itemCusomLineItems;

    return (
        <div>
            <div className="my-6 flex items-center">
                <h3 className="mr-4">Custom Line Items</h3>
                <Switch
                    size="medium"
                    data-testid="custom-line-item-list-toggle"
                    isChecked={enableCustomLineItems}
                    onChange={handleToggleEnableCustomLineItems}
                />

                {enableCustomLineItems && hasCustomLineItems ? (
                    <SecondaryButton
                        className="ml-auto"
                        size="small"
                        data-testid="custom-line-item-list-edit-button"
                        onClick={() => setIsModalOpen(true)}
                    >
                        <EditIcon />
                    </SecondaryButton>
                ) : null}
            </div>

            {isModalOpen && (
                <EditCustomLineItemListModal
                    item={pkgItem}
                    itemIndex={itemIndex}
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                />
            )}

            {enableCustomLineItems && !hasCustomLineItems ? (
                <Alert color="primary">
                    <div className="flex items-center justify-between">
                        <div>You have not added any custom line items</div>

                        <Button color="primary" size="small" icon={<PlusIcon />} onClick={() => setIsModalOpen(true)}>
                            Line Item
                        </Button>
                    </div>
                </Alert>
            ) : null}

            {enableCustomLineItems &&
                (customeLineItems ?? []).map((lineItem, index) => (
                    <div
                        key={uniqueId("lineItem")}
                        className="mt-4 flex items-center justify-between"
                        data-testid="custom-line-item-list-row"
                    >
                        <div>
                            <NoTranslate as="div" className="font-bold">
                                {lineItem.name}
                            </NoTranslate>

                            <div className="text-gray-dark">
                                <Currency currency={seller.currency}>{lineItem.price!}</Currency> / item
                            </div>
                        </div>

                        <QuantitySpinner
                            size="large"
                            value={lineItem.quantity!}
                            min={1}
                            onChange={(value) => handleChangeQuantity(index, value)}
                        />
                    </div>
                ))}
        </div>
    );
};

interface EditCustomLineItemListModalProps {
    item?: CreatePurchaseItemState;
    itemIndex: number;
    isOpen: boolean;
    onClose: () => void;
}

const EditCustomLineItemListModal = ({ item, itemIndex, isOpen, onClose }: EditCustomLineItemListModalProps) => {
    return (
        <Modal size="huge" isOpen={isOpen} onClose={onClose}>
            <Modal.Header>Custom Line Items</Modal.Header>
            <ModalContent item={item} itemIndex={itemIndex} onClose={onClose} />
        </Modal>
    );
};

interface ModalContentProps {
    itemIndex: number;
    item?: CreatePurchaseItemState;
    onClose: () => void;
}

const ModalContent = ({ item: pkgItem, itemIndex, onClose }: ModalContentProps) => {
    const activeItem = useCreatePurchaseStore((state) => state.getActiveItem());
    const item = pkgItem ?? activeItem;
    const setPurchase = useCreatePurchaseStore((state) => state.actions.setPurchase);

    const handleSubmit = (values: FormValues) => {
        if (isPackageItem(item)) {
            setPurchase((draft) => {
                const index = findItemIndex(draft, item);
                draft.items[index].customLineItems = values.lineItems;
            });
        } else {
            setPurchase((purchase) => {
                purchase.items[itemIndex].customLineItems = values.lineItems;
            });
        }

        onClose();
    };

    return (
        <XolaFormik
            initialValues={{
                lineItems: (item.customLineItems ?? []).length > 0 ? item.customLineItems : [makeLineItem()],
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {({ values, handleSubmit }) => (
                <>
                    <Modal.Body className="grid grid-cols-2 gap-8">
                        <SimpleBar className="p-1" style={{ maxHeight: 500 }}>
                            <div className="mb-4 space-y-4 divide-y divide-gray-light">
                                {values.lineItems.map((lineItem, index) => (
                                    <CustomLineItem
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={index}
                                        index={index}
                                        isFirst={index === 0}
                                        lineItem={lineItem}
                                    />
                                ))}
                            </div>

                            <FieldArray name="lineItems">
                                {(helpers) => (
                                    <SecondaryButton
                                        icon={<PlusIcon />}
                                        onClick={() => {
                                            helpers.push(makeLineItem());
                                        }}
                                    >
                                        Add Line Item
                                    </SecondaryButton>
                                )}
                            </FieldArray>
                        </SimpleBar>

                        <Breakdown itemIndex={itemIndex} item={item} />
                    </Modal.Body>
                    <Modal.Footer>
                        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                        <SubmitButton type="submit" onClick={handleSubmit}>
                            Save
                        </SubmitButton>
                    </Modal.Footer>
                    <ScrollToFieldError />
                </>
            )}
        </XolaFormik>
    );
};

const Breakdown = ({ itemIndex: initialIndex, item }: { itemIndex: number; item: CreatePurchaseItemState }) => {
    const purchase = useCreatePurchaseStore((state) => state.purchase);
    const { values } = useFormikContext<FormValues>();
    const updatedPurchase = useUpdatePurchase();

    // Dynamically calculate item index if package
    const itemIndex = isPackageItem(item) ? findItemIndex(purchase, item) : initialIndex;

    // Compute valid custom line items from form values
    const validCustomLineItems = values.lineItems.filter((item) => item.amount && item.name);

    const [customLineItems, setCustomLineItems] = useState(validCustomLineItems);

    const payload = createPurchaseStoreToPayload(purchase);

    const payloadItemLineItems = payload.items[itemIndex].lineItems.filter(
        (lineItem: LineItem) => ![LineItemType.ADJUSTMENT, LineItemType.TAXED_ADJUSTMENT].includes(lineItem.type),
    );

    payload.items[itemIndex].lineItems = payloadItemLineItems;

    const [purchaseBreakdown, setPurchaseBreakdown] = useState<Purchase>(
        usePurchaseResponseStore.getState().purchase as Purchase,
    );

    const shouldSetCustomLineItems = !isEqual(customLineItems, validCustomLineItems);

    useEffect(() => {
        if (shouldSetCustomLineItems) {
            setCustomLineItems(validCustomLineItems);
        }
    }, [validCustomLineItems, shouldSetCustomLineItems]);

    const itemsKey = JSON.stringify(
        customLineItems.map((index) => ({
            name: index.name,
            amount: index.amount,
            quantity: index.quantity,
            type: index.type,
        })),
    );
    const [debouncedItemsKey] = useDebounce(itemsKey, 500);

    const updatePurchase = async () => {
        try {
            if (!payload) {
                return;
            }

            const updatedItems = produce(payload.items, (items: any) => {
                items[itemIndex].lineItems = [...items[itemIndex].lineItems, ...customLineItems];
            });

            const purchaseResponse = await updatedPurchase.mutateAsync({ ...payload, items: updatedItems });
            setPurchaseBreakdown(purchaseResponse);
        } catch (error) {
            console.error("Failed to update breakdown for custom lineitems", error);
        }
    };

    useEffect(() => {
        updatePurchase();
        // adding payload/updatedPurchase is causing infinite loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedItemsKey]);

    return (
        <div className="rounded-lg bg-gray-lighter p-8">
            <h3 className="mb-6">Breakdown</h3>

            {purchaseBreakdown && purchaseBreakdown.items.length > itemIndex ? (
                <CreatePurchaseBreakdown item={purchaseBreakdown.items[itemIndex]} />
            ) : null}
        </div>
    );
};

interface CustomLineItemProps {
    index: number;
    lineItem: Partial<LineItem>;
    isFirst: boolean;
}

const CustomLineItem = ({ index, lineItem, isFirst }: CustomLineItemProps) => {
    const { setFieldValue } = useFormikContext<FormValues>();
    const price = lineItem.price ?? 0;
    const quantity = lineItem.quantity ?? 0;
    const absolutePrice = get(lineItem, "absolutePrice") ?? 0;
    const [multiplier, setMultiplier] = useState(price < 0 ? -1 : 1);

    const handleMultiplierChange = (e: any) => {
        const multiplier = Number(e.target.value);
        setMultiplier(multiplier);
        setFieldValue(`lineItems.${index}.price`, Math.abs(price) * multiplier);
    };

    useEffect(() => {
        // Sync `price` when `absolutePrice` changes
        setFieldValue(`lineItems.${index}.price`, absolutePrice * multiplier);
    }, [absolutePrice, index, multiplier, setFieldValue]);

    useEffect(() => {
        // Sync `amount` when `price` or `quantity` changes
        setFieldValue(`lineItems.${index}.amount`, price * quantity);
    }, [price, quantity, index, setFieldValue]);

    return (
        <div data-testid={`custom-line-item-list-${index + 1}`} className={clsx(isFirst || "pt-4")}>
            <FormField label="Item" name={`lineItems.${index}.name`}>
                <div className="flex space-x-2">
                    <InputField />

                    <FieldArray name="lineItems">
                        {(helpers) => (
                            <SecondaryButton
                                data-testid="custom-line-item-list-remove-button"
                                onClick={() => helpers.remove(index)}
                            >
                                <TrashIcon />
                            </SecondaryButton>
                        )}
                    </FieldArray>
                </div>
            </FormField>

            <div className="flex space-x-2">
                <FormGroup className="flex-1">
                    <Label>Type</Label>

                    <Select value={multiplier} onChange={handleMultiplierChange}>
                        <option value={1}>Charge</option>
                        <option value={-1}>Discount</option>
                    </Select>
                </FormGroup>

                <FormField className="flex-1" label="Amount" name={`lineItems.${index}.absolutePrice`}>
                    <CurrencyInputField />
                </FormField>

                <FormGroup>
                    <Label>Quantity</Label>

                    <QuantitySpinner
                        size="large"
                        value={lineItem.quantity ?? 1}
                        min={1}
                        onChange={(value) => {
                            setFieldValue(`lineItems.${index}.quantity`, value);
                        }}
                    />
                </FormGroup>
            </div>

            <Switch.Group>
                <Switch
                    isChecked={lineItem.type === LineItemType.TAXED_ADJUSTMENT}
                    onChange={(value: boolean) => {
                        setFieldValue(
                            `lineItems.${index}.type`,
                            value ? LineItemType.TAXED_ADJUSTMENT : LineItemType.ADJUSTMENT,
                        );
                    }}
                />

                <Switch.Label className="ml-2">Is Taxed?</Switch.Label>
            </Switch.Group>
        </div>
    );
};

const validationSchema = object({
    lineItems: array(
        object({
            name: requiredString(),
            absolutePrice: requiredNumberMoreThanZero(),
        }),
    ),
});

const makeLineItem = (values?: Partial<LineItem>): Partial<LineItem> => {
    return {
        name: "",
        quantity: 1,
        type: LineItemType.ADJUSTMENT,
        includeInAmount: true,
        destination: LineItemDestination.SELLER,
        ...values,
    };
};
